import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';

interface MerchantStatusRow {
  merchantId: string;
  name: string;
  type: string;
  merchantGroupId?: string;
  merchantGroupName?: string;
  running: number;
  partial: number;
  notRunning: number;
  total: number;
}

interface MerchantGroupOption {
  id: string;
  name: string;
}

interface MerchantTypeOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-tiktok-ad-merchant-list',
  templateUrl: './tiktok-ad-merchant-list.component.html',
  styleUrls: ['./tiktok-ad-merchant-list.component.css']
})
export class TikTokAdMerchantListComponent implements OnInit {
  rows: MerchantStatusRow[] = [];
  groupOptions: MerchantGroupOption[] = [];
  merchantTypeOptions: MerchantTypeOption[] = [];
  merchantOptions: MerchantStatusRow[] = [];
  isLoading = false;
  selectedMerchantType = '';
  selectedGroupId = '';
  selectedMerchantId = '';
  sortBy = 'name';
  sortDir: 'asc' | 'desc' = 'asc';

  summary = {
    running: 0,
    partial: 0,
    notRunning: 0,
    total: 0
  };

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;

    let params = new HttpParams()
      .set('sortBy', this.sortBy)
      .set('sortDir', this.sortDir);

    this.http.get<any>('/api/TikTokAd/MerchantStatusSummary', { params }).subscribe(
      response => {
        this.rows = response?.data || [];
        this.summary = {
          running: response?.summary?.running || 0,
          partial: response?.summary?.partial || 0,
          notRunning: response?.summary?.notRunning || 0,
          total: response?.summary?.total || 0
        };
        this.rebuildFilterOptions();
        this.isLoading = false;
      },
      _ => {
        this.rows = [];
        this.groupOptions = [];
        this.merchantTypeOptions = [];
        this.merchantOptions = [];
        this.summary = { running: 0, partial: 0, notRunning: 0, total: 0 };
        this.isLoading = false;
      }
    );
  }

  onSearch(): void {
    // ng-select filters client-side via selected value.
  }

  toggleSort(col: string): void {
    if (this.sortBy === col) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col;
      this.sortDir = 'desc';
    }
  }

  sortIcon(col: string): string {
    if (this.sortBy !== col) return 'fa-sort text-muted';
    return this.sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  formatWithPercent(value: number, total: number): string {
    if (!total) return `${value} (0%)`;
    const pct = Math.round((value / total) * 100);
    return `${value} (${pct}%)`;
  }

  formatRowWithPercent(value: number, rowTotal: number): string {
    if (!rowTotal) return `${value} (0%)`;
    const pct = Math.round((value / rowTotal) * 100);
    return `${value} (${pct}%)`;
  }

  get filteredRows(): MerchantStatusRow[] {
    let filtered = this.rows;
    if (this.selectedMerchantType) {
      filtered = filtered.filter(r => (r.type || '') === this.selectedMerchantType);
    }
    if (this.selectedGroupId) {
      filtered = filtered.filter(r => (r.merchantGroupId || '') === this.selectedGroupId);
    }
    if (!this.selectedMerchantId) {
      return this.sortRows(filtered);
    }
    return this.sortRows(filtered.filter(r => r.merchantId === this.selectedMerchantId));
  }

  private sortRows(rows: MerchantStatusRow[]): MerchantStatusRow[] {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const aVal = this.getSortValue(a, this.sortBy);
      const bVal = this.getSortValue(b, this.sortBy);

      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;

      // Stable tie-breaker by merchant name for consistent display
      return (a.name || '').localeCompare(b.name || '');
    });
    return sorted;
  }

  private getSortValue(row: MerchantStatusRow, col: string): number | string {
    switch (col) {
      case 'running':
        return this.toRatio(row.running, row.total);
      case 'partial':
        return this.toRatio(row.partial, row.total);
      case 'notRunning':
        return this.toRatio(row.notRunning, row.total);
      case 'total':
        return row.total || 0;
      case 'type':
        return row.type || '';
      case 'name':
      default:
        return row.name || '';
    }
  }

  private toRatio(value: number, total: number): number {
    if (!total) return 0;
    return value / total;
  }

  private buildGroupOptions(sourceRows: MerchantStatusRow[]): MerchantGroupOption[] {
    const map = new Map<string, string>();
    for (const row of sourceRows) {
      if (!row.merchantGroupId) {
        continue;
      }
      map.set(row.merchantGroupId, row.merchantGroupName || 'Không có tên nhóm');
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private buildMerchantOptions(sourceRows: MerchantStatusRow[]): MerchantStatusRow[] {
    let options = sourceRows;
    if (this.selectedMerchantType) {
      options = sourceRows.filter(r => (r.type || '') === this.selectedMerchantType);
    }
    if (!this.selectedGroupId) {
      return options;
    }
    return options.filter(r => (r.merchantGroupId || '') === this.selectedGroupId);
  }

  get filteredSummary() {
    return {
      running: this.filteredRows.reduce((sum, r) => sum + r.running, 0),
      partial: this.filteredRows.reduce((sum, r) => sum + r.partial, 0),
      notRunning: this.filteredRows.reduce((sum, r) => sum + r.notRunning, 0),
      total: this.filteredRows.reduce((sum, r) => sum + r.total, 0)
    };
  }

  onGroupChange(): void {
    this.rebuildFilterOptions();
    if (this.selectedMerchantId) {
      const existsInGroup = this.merchantOptions.some(m => m.merchantId === this.selectedMerchantId);
      if (!existsInGroup) {
        this.selectedMerchantId = '';
      }
    }
  }

  private buildMerchantTypeOptions(sourceRows: MerchantStatusRow[]): MerchantTypeOption[] {
    const knownOrder = ['TikTok', 'TikTokUk', 'TikTokDe'];
    const available = new Set(sourceRows.map(r => r.type).filter(t => !!t));
    return knownOrder
      .filter(t => available.has(t))
      .map(t => ({ id: t, name: t }));
  }

  onMerchantTypeChange(): void {
    this.rebuildFilterOptions();
    this.onGroupChange();
  }

  private rebuildFilterOptions(): void {
    this.merchantTypeOptions = this.buildMerchantTypeOptions(this.rows);
    this.groupOptions = this.buildGroupOptions(this.rows);
    this.merchantOptions = this.buildMerchantOptions(this.rows);
  }

  openMerchantInTikTokAds(row: MerchantStatusRow): void {
    if (!row?.merchantId) {
      return;
    }
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/tiktok-products'], {
        queryParams: { merchantId: row.merchantId }
      })
    );
    window.open(url, '_blank');
  }
}
